import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  IconButton,
  Stack,
  CircularProgress,
  Button,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import NightsStayIcon from "@mui/icons-material/NightsStay";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { amenityBookingCalenderList } from "Apis/amenity";
import AddIcon from "@mui/icons-material/Add";
// ─── Color Palette ───────────────────────────────────────────────────────────
const COLORS = {
  primary: "#A68181",
  light: "#FFF2F2",
  medium: "#C4A0A0",
  dark: "#7A5C5C",
  white: "#FFFFFF",
  border: "#E8D0D0",
  text: "#4A3535",
  subtext: "#8B6B6B",
  morningBg: "#EBF3FF",
  morningText: "#185FA5",
  morningBorder: "#B5D4F4",
  eveningBg: "#FFF0F5",
  eveningText: "#A68181",
  eveningBorder: "#F4C0D1",
  blockedBg: "#F1EFE8",
  blockedText: "#5F5E5A",
  blockedBorder: "#D3D1C7",
  bookedBg: "#FCEBEB",
  bookedText: "#A32D2D",
  bookedBorder: "#F7C1C1",
  availBg: "#EAF3DE",
  availText: "#3B6D11",
  availBorder: "#C0DD97",
  highAvail: "#3B6D11",
  highAvailBg: "#EAF3DE",
  midAvail: "#854F0B",
  midAvailBg: "#FAEEDA",
  lowAvail: "#993C1D",
  lowAvailBg: "#FFF2F2",
  noAvail: "#A32D2D",
  noAvailBg: "#FCEBEB",
};

// ─── Slot Config ─────────────────────────────────────────────────────────────
const SLOT_INFO = {
  1: {
    key: "slot_1",
    label: "Morning Slot",
    time: "8:00 AM - Next day 7:00 AM",
    color: COLORS.morningText,
    bg: COLORS.morningBg,
    border: COLORS.morningBorder,
    icon: <WbSunnyIcon sx={{ fontSize: 14 }} />,
  },
  2: {
    key: "slot_2",
    label: "Evening Slot",
    time: "4:00 PM - Next day 3:00 PM",
    color: COLORS.eveningText,
    bg: COLORS.eveningBg,
    border: COLORS.eveningBorder,
    icon: <NightsStayIcon sx={{ fontSize: 14 }} />,
  },
};

// ─── Day availability style from backend status ───────────────────────────────
// Backend: 0 = fully available, 1 = partially booked, 2 = fully booked
function getDayAvailStyle(status) {
  if (status === 0) return { bg: COLORS.highAvailBg, text: COLORS.highAvail, label: "2 Slots" };
  if (status === 1) return { bg: COLORS.midAvailBg, text: COLORS.midAvail, label: "1 Slot" };
  return { bg: COLORS.noAvailBg, text: COLORS.noAvail, label: "Fully Booked" };
}

// ─── Past date check (strict: before today, not including today) ─────────────
function isPastDate(year, month, day) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(year, month - 1, day);
  return target < today;
}

// ─── Past date cell badge ─────────────────────────────────────────────────────
// For past dates show booked count instead of availability slots
function getPastDayStyle(dayData) {
  const slot1Booked = dayData?.slot_1?.status === 2;
  const slot2Booked = dayData?.slot_2?.status === 2;
  const bookedCount = (slot1Booked ? 1 : 0) + (slot2Booked ? 1 : 0);
  if (bookedCount === 0) return { bg: "", text: "#9E9E9E", label: "" };
  if (bookedCount === 1) return { bg: COLORS.midAvailBg, text: COLORS.midAvail, label: "1 Booked" };
  return { bg: COLORS.bookedBg, text: COLORS.bookedText, label: "2 Booked" };
}

// ─── Booking Detail (shown inside slot card when booked) ─────────────────────
function SlotBookingDetail({ booking }) {
  const amenities = booking?.amenity || [];
  const addOns = booking?.add_ons || [];

  return (
    <Box sx={{ mt: 1.2, pt: 1.2, borderTop: `1px dashed ${COLORS.bookedBorder}` }}>
      {/* Member Name */}
      <Stack direction="row" spacing={0.8} alignItems="center" mb={0.5}>
        <PersonIcon sx={{ fontSize: 13, color: COLORS.primary }} />
        <Typography sx={{ fontSize: 11, color: COLORS.text, fontWeight: 600 }}>
          {booking?.member_name || "—"}
        </Typography>
      </Stack>

      {/* Phone */}
      <Stack direction="row" spacing={0.8} alignItems="center" mb={0.5}>
        <PhoneIcon sx={{ fontSize: 13, color: COLORS.primary }} />
        <Typography sx={{ fontSize: 11, color: COLORS.text }}>
          {booking?.member_mobile_number ?? "—"}
        </Typography>
      </Stack>

      {/* Time range */}
      {/* {booking?.start_time && booking?.end_time && (
        <Stack direction="row" spacing={0.8} alignItems="center" mb={0.5}>
          <AccessTimeIcon sx={{ fontSize: 13, color: COLORS.primary }} />
          <Typography sx={{ fontSize: 11, color: COLORS.text }}>
            {booking?.start_time} - {booking?.end_time}
          </Typography>
        </Stack>
      )} */}

      {/* Amenities */}
      {amenities?.length > 0 && (
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          mt={0.3}
        >
          <MeetingRoomIcon sx={{ fontSize: 13, color: COLORS.primary }} />
          {amenities?.map((a) => (
            <Chip
              key={a}
              label={a}
              size="small"
              sx={{
                bgcolor: COLORS.light,
                color: COLORS.dark,
                fontWeight: 500,
                fontSize: 9,
                height: 18,
                border: `1px solid ${COLORS.border}`,
              }}
            />
          ))}
        </Stack>
      )}

      {/* Add-ons */}
      {addOns?.length > 0 && (
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          mt={0.5}
        >
          {addOns?.map((a) => (
            <Chip
              key={a}
              label={a}
              size="small"
              sx={{
                bgcolor: "#F3F0FF",
                color: "#5B3FA6",
                fontWeight: 500,
                fontSize: 9,
                height: 18,
                border: `1px solid #C4B5F4`,
              }}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
}

// ─── Slot Card ────────────────────────────────────────────────────────────────
// slotData shape: { status: 0|1|2, booking_details: {...} }
// status: 0 = available, 1 = blocked, 2 = booked
function SlotCard({ slotNum, slotData, selectedDateKey }) {
  const navigate = useNavigate();
  const info = SLOT_INFO[slotNum];
  const slotStatus = slotData?.status ?? 0;
  const isBooked = slotStatus === 2;
  const isBlocked = slotStatus === 1;

  const booking = isBooked ? slotData?.booking_details : null;

  const handleAddBooking = () => {
    navigate("/amenity/booking/add", {
      state: {
        bookingDate: selectedDateKey,
        slotNum,
      },
    });
  };

  let cardBg, cardBorder, leftBorderColor, statusIcon;

  if (isBooked) {
    cardBg = COLORS.bookedBg;
    cardBorder = COLORS.bookedBorder;
    leftBorderColor = COLORS.bookedText;
    statusIcon = (
      <Chip
        label="Booked"
        size="small"
        sx={{
          bgcolor: "#F7C1C1",
          color: COLORS.bookedText,
          fontWeight: 700,
          fontSize: 10,
          height: 20,
          border: `1px solid ${COLORS.bookedBorder}`,
        }}
      />
    );
  } else if (isBlocked) {
    cardBg = COLORS.blockedBg;
    cardBorder = COLORS.blockedBorder;
    leftBorderColor = COLORS.blockedText;
    statusIcon = (
      <Chip
        icon={
          <BlockIcon
            sx={{ fontSize: "11px !important", color: `${COLORS.blockedText} !important` }}
          />
        }
        label="Blocked"
        size="small"
        sx={{
          bgcolor: COLORS.blockedBg,
          color: COLORS.blockedText,
          fontWeight: 700,
          fontSize: 10,
          height: 20,
          border: `1px solid ${COLORS.blockedBorder}`,
          "& .MuiChip-icon": { ml: "5px" },
        }}
      />
    );
  } else {
    cardBg = info.bg;
    cardBorder = info.border;
    leftBorderColor = info.color;
    statusIcon = (
      <Chip
        icon={
          <CheckCircleOutlineIcon
            sx={{ fontSize: "11px !important", color: `${COLORS.availText} !important` }}
          />
        }
        label="Available"
        size="small"
        sx={{
          bgcolor: COLORS.availBg,
          color: COLORS.availText,
          fontWeight: 700,
          fontSize: 10,
          height: 20,
          border: `1px solid ${COLORS.availBorder}`,
          "& .MuiChip-icon": { ml: "5px" },
        }}
      />
    );
  }

  return (
    <Box
      onClick={!isBooked && !isBlocked ? handleAddBooking : undefined}
      sx={{
        border: `1px solid ${cardBorder}`,
        borderLeft: `3px solid ${leftBorderColor}`,
        borderRadius: 2,
        p: 1.5,
        mb: 1,
        bgcolor: cardBg,
        transition: "box-shadow 0.15s, transform 0.15s",
        cursor: !isBooked && !isBlocked ? "pointer" : "default",
        "&:hover":
          !isBooked && !isBlocked
            ? {
                boxShadow: `0 6px 18px ${COLORS.primary}20`,
                transform: "translateY(-1px)",
              }
            : isBooked
              ? { boxShadow: `0 3px 12px ${COLORS.primary}20` }
              : {},
      }}
    >
      {/* Slot header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" alignItems="center" gap={1}>
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: 1.5,
              bgcolor: COLORS.white,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: isBlocked ? COLORS.blockedText : leftBorderColor,
              flexShrink: 0,
            }}
          >
            {isBlocked ? <BlockIcon sx={{ fontSize: 16 }} /> : info.icon}
          </Box>
          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: COLORS.text, lineHeight: 1.2 }}>
              {info?.label}
            </Typography>
            <Stack direction="row" spacing={0.5} alignItems="center" mt={0.2}>
              <AccessTimeIcon sx={{ fontSize: 11, color: COLORS.subtext }} />
              <Typography sx={{ fontSize: 10, color: COLORS.subtext }}>{info?.time}</Typography>
            </Stack>
          </Box>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          {statusIcon}
        </Stack>
      </Stack>

      {/* Booking details */}
      {isBooked && booking && Object.keys(booking)?.length > 0 && (
        <SlotBookingDetail booking={booking} />
      )}
    </Box>
  );
}
const formatDateKey = (day, currentYear, currentMonth) =>
  day
    ? `${currentYear}-${String(currentMonth).padStart(2, "0")}-${String(day).padStart(2, "0")}`
    : null;

// ─── Main Calendar ────────────────────────────────────────────────────────────
export default function BookingCalendar() {
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth() + 1);
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const day = today.getDate();
  const month = today.getMonth() + 1;
  // Map of "YYYY-MM-DD" -> full day object from backend
  const [calendarMap, setCalendarMap] = useState({});
  const getDayData = (day, currentYear, currentMonth) => {
    const key = formatDateKey(day, currentYear, currentMonth);
    return key ? calendarMap[key] || null : null;
  };

  // FIX: store only the selected day number. The day's data is derived from
  // calendarMap on every render, so it updates as soon as the API responds.
  const [selectedDay, setSelectedDay] = useState(day);
  const selectedDate = selectedDay
    ? {
        day: selectedDay,
        key: formatDateKey(selectedDay, currentYear, currentMonth),
        data: getDayData(selectedDay, currentYear, currentMonth),
        isPast: isPastDate(currentYear, currentMonth, selectedDay),
      }
    : null;

  const [loading, setLoading] = useState(false);

  const MONTH_NAMES = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  // ── Single API call per month ─────────────────────────────────────────────
  const getBookingData = async () => {
    setLoading(true);
    try {
      const res = await amenityBookingCalenderList({ month: currentMonth, year: currentYear });
      if (res?.status === 200) {
        const data = res?.data?.data || [];
        // Build lookup map keyed by date string
        const map = {};
        data.forEach((item) => {
          if (item?.date) map[item?.date] = item;
        });
        setCalendarMap(map);
      }
    } catch (err) {
      console.error("Failed to fetch bookings", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentMonth !== month || currentYear !== today.getFullYear()) {
      setSelectedDay(1); // other months: auto-select the 1st date
    } else {
      setSelectedDay(day); // current month: auto-select today
    }
    getBookingData();
  }, [currentMonth, currentYear]);

  // ── Calendar grid helpers ─────────────────────────────────────────────────
  const firstDay = new Date(currentYear, currentMonth - 1, 1).getDay();
  const daysInMonth = new Date(currentYear, currentMonth, 0).getDate();

  const calendarCells = [];
  for (let i = 0; i < firstDay; i++) calendarCells.push(null);
  for (let d = 1; d <= daysInMonth; d++) calendarCells.push(d);

  const isToday = (day) =>
    day === today.getDate() &&
    currentMonth === today.getMonth() + 1 &&
    currentYear === today.getFullYear();

  const isSelected = (day) => selectedDay === day;

  const handlePrevMonth = () => {
    if (currentMonth === 1) {
      setCurrentMonth(12);
      setCurrentYear((y) => y - 1);
    } else setCurrentMonth((m) => m - 1);
  };
  const handleNextMonth = () => {
    if (currentMonth === 12) {
      setCurrentMonth(1);
      setCurrentYear((y) => y + 1);
    } else setCurrentMonth((m) => m + 1);
  };

  const handleDayClick = (day) => {
    if (!day) return;
    setSelectedDay(day);
  };

  const selectedData = selectedDate?.data || null;
  console.log("selectedDate", selectedDate);
  console.log("selectedData", selectedData);
  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, fontFamily: "'Georgia', serif" }}>
      {/* Page Header */}
      <Stack direction="row" spacing={1.5} alignItems="center" mb={3}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            bgcolor: COLORS.primary,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CalendarTodayIcon sx={{ color: COLORS.white, fontSize: 20 }} />
        </Box>
        <Typography sx={{ fontWeight: 700, fontSize: 22, color: COLORS.dark, letterSpacing: -0.5 }}>
          Booking Calendar
        </Typography>
      </Stack>

      <Grid container spacing={2.5}>
        {/* ── LEFT: Calendar ──────────────────────────────────────────── */}
        <Grid item xs={12} lg={8}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 4,
              border: `1.5px solid ${COLORS.border}`,
              overflow: "hidden",
              bgcolor: COLORS.white,
            }}
          >
            {/* Month Nav */}
            <Box
              sx={{
                background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.dark})`,
                px: 2.5,
                py: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <IconButton
                onClick={handlePrevMonth}
                size="small"
                sx={{ color: COLORS.white, "&:hover": { bgcolor: "rgba(255,255,255,0.15)" } }}
              >
                <ChevronLeftIcon />
              </IconButton>
              <Typography
                sx={{ fontWeight: 700, fontSize: 18, color: COLORS.white, letterSpacing: 0.5 }}
              >
                {MONTH_NAMES[currentMonth - 1]} {currentYear}
              </Typography>
              <IconButton
                onClick={handleNextMonth}
                size="small"
                sx={{ color: COLORS.white, "&:hover": { bgcolor: "rgba(255,255,255,0.15)" } }}
              >
                <ChevronRightIcon />
              </IconButton>
            </Box>

            {/* Day headers */}
            <Box sx={{ px: 2, pt: 2 }}>
              <Grid container>
                {DAY_NAMES?.map((d) => (
                  <Grid item key={d} xs={12 / 7}>
                    <Typography
                      sx={{
                        textAlign: "center",
                        fontSize: 11,
                        fontWeight: 700,
                        color: COLORS.subtext,
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                        pb: 1,
                      }}
                    >
                      {d}
                    </Typography>
                  </Grid>
                ))}
              </Grid>

              {/* Calendar cells */}
              <Grid container sx={{ pb: 2 }}>
                {calendarCells?.map((day, idx) => {
                  const dayData = getDayData(day, currentYear, currentMonth);
                  const pastFlag = day ? isPastDate(currentYear, currentMonth, day) : false;
                  // Past dates: show booked count badge; future/today: show availability
                  const availStyle = day
                    ? pastFlag
                      ? getPastDayStyle(dayData)
                      : getDayAvailStyle(dayData?.status ?? 0)
                    : null;
                  const todayFlag = isToday(day);
                  const selectedFlag = isSelected(day);

                  return (
                    <Grid item key={idx} xs={12 / 7}>
                      <Box
                        onClick={() => day && handleDayClick(day)}
                        sx={{
                          m: "2px",
                          borderRadius: 2,
                          minHeight: { xs: 70, sm: 80 },
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          pt: 1,
                          pb: 0.5,
                          px: 0.5,
                          cursor: day ? "pointer" : "default",
                          transition: "all 0.15s ease",
                          bgcolor: selectedFlag ? COLORS.primary : "transparent",
                          border: selectedFlag
                            ? `2px solid ${COLORS.dark}`
                            : todayFlag
                              ? `2px solid ${COLORS.primary}`
                              : "2px solid transparent",
                          "&:hover": day
                            ? {
                                bgcolor: selectedFlag ? COLORS.primary : `${COLORS.primary}18`,
                                transform: "scale(1.04)",
                              }
                            : {},
                        }}
                      >
                        {day && (
                          <>
                            <Typography
                              sx={{
                                fontSize: 13,
                                fontWeight: selectedFlag || todayFlag ? 700 : 500,
                                color: selectedFlag
                                  ? COLORS.white
                                  : todayFlag
                                    ? COLORS.primary
                                    : COLORS.text,
                                lineHeight: 1,
                                mb: 0.6,
                              }}
                            >
                              {day}
                            </Typography>
                            <Box
                              sx={{
                                px: 0.8,
                                py: 0.2,
                                borderRadius: 1,
                                mb: 0.5,
                                bgcolor: selectedFlag
                                  ? availStyle?.label !== ""
                                    ? "rgba(255,255,255,0.22)"
                                    : ""
                                  : availStyle.bg,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: 12,
                                  textAlign: "center",
                                  padding: "3px 6px",
                                  fontWeight: 700,
                                  color: selectedFlag ? COLORS.white : availStyle.text,
                                  lineHeight: 1,
                                  whiteSpace: "wrap",
                                }}
                              >
                                {availStyle?.label}
                              </Typography>
                            </Box>
                          </>
                        )}
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>

            {loading && (
              <Box
                sx={{
                  textAlign: "center",
                  pb: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                }}
              >
                <CircularProgress size={12} sx={{ color: COLORS.primary }} />
                <Typography sx={{ fontSize: 11, color: COLORS.subtext, fontStyle: "italic" }}>
                  Loading bookings…
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* ── RIGHT: Details ──────────────────────────────────────────── */}
        <Grid item xs={12} lg={4}>
          {!selectedDate ? (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 4,
                border: `1.5px dashed ${COLORS.border}`,
                bgcolor: COLORS.white,
                height: "100%",
                minHeight: 300,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                p: 4,
              }}
            >
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  bgcolor: COLORS.light,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 2,
                }}
              >
                <CalendarTodayIcon sx={{ fontSize: 28, color: COLORS.primary }} />
              </Box>
              <Typography sx={{ fontWeight: 600, fontSize: 16, color: COLORS.dark, mb: 0.5 }}>
                Select a Date
              </Typography>
              <Typography sx={{ fontSize: 13, color: COLORS.subtext, textAlign: "center" }}>
                Click any date to view slot availability and bookings
              </Typography>
            </Paper>
          ) : (
            <Box>
              {/* Date header */}
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: `1.5px solid ${COLORS.border}`,
                  bgcolor: COLORS.white,
                  p: 2,
                  mb: 1.5,
                }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography sx={{ fontWeight: 700, fontSize: 17, color: COLORS.dark }}>
                    {String(selectedDate?.day).padStart(2, "0")} {MONTH_NAMES[currentMonth - 1]}{" "}
                    {currentYear}
                  </Typography>
                  {selectedDate?.isPast && (
                    <Chip
                      label="Past"
                      size="small"
                      sx={{
                        bgcolor: "#F5F5F5",
                        color: "#9E9E9E",
                        fontWeight: 700,
                        fontSize: 10,
                        height: 20,
                        border: "1px solid #E0E0E0",
                      }}
                    />
                  )}
                </Stack>
                <Typography sx={{ fontSize: 12, color: COLORS.subtext, mt: 0.3 }}>
                  {selectedDate?.isPast
                    ? (() => {
                        const s1 = selectedData?.slot_1?.status === 2;
                        const s2 = selectedData?.slot_2?.status === 2;
                        const count = (s1 ? 1 : 0) + (s2 ? 1 : 0);
                        return count === 0
                          ? "No bookings on this date"
                          : `${count} booking${count > 1 ? "s" : ""} on this date`;
                      })()
                    : selectedData
                      ? selectedData?.status === 2
                        ? "Fully booked on this date"
                        : selectedData?.status === 1
                          ? "Partially booked on this date"
                          : "All slots available on this date"
                      : "No booking data for this date"}
                </Typography>
              </Paper>

              {/* Slot cards */}
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: `1.5px solid ${COLORS.border}`,
                  bgcolor: COLORS.white,
                  p: 2,
                  mb: 1.5,
                }}
              >
                <Typography sx={{ fontWeight: 600, fontSize: 13, color: COLORS.dark, mb: 1.2 }}>
                  {selectedDate?.isPast ? "Booking History" : "Slot Availability"}
                </Typography>

                {[1, 2].map((slotNum) => {
                  const info = SLOT_INFO[slotNum];
                  const slotData = selectedData?.[info.key] ?? { status: 0, booking_details: {} };

                  // For past dates: only render the card if the slot was actually booked
                  if (selectedDate?.isPast && slotData?.status !== 2) return null;

                  return (
                    <SlotCard
                      key={slotNum}
                      slotNum={slotNum}
                      slotData={slotData}
                      selectedDateKey={selectedDate?.key}
                    />
                  );
                })}

                {/* Past date with zero bookings */}
                {selectedDate?.isPast &&
                  selectedData?.slot_1?.status !== 2 &&
                  selectedData?.slot_2?.status !== 2 && (
                    <Box
                      sx={{
                        textAlign: "center",
                        py: 3,
                        color: COLORS.subtext,
                        fontSize: 13,
                        fontFamily: "roboto, sans-serif",
                      }}
                    >
                      No bookings on this date
                    </Box>
                  )}
              </Paper>
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}
